import { AttachType } from '../models/request-and-responses/rest-api/enums/attachType';
import { SafeResourceUrl } from '@angular/platform-browser';
import { IShadowStorageFileAttach } from '../models/request-and-responses/rest-api/interfaces/shadowStorageFileAttach';
// @ts-ignore
const uuidv5 = require('uuid/v1');

export interface IContractAttachment {
    id: number;
    name: string;
    type: AttachType;
    label: string;
    loading: boolean;
    uuid: string;
    uploadDateTime: Date;
    safeUrl: SafeResourceUrl;
    chiamataPreAdesioneDateTime: Date;
}

export class Attachment implements IContractAttachment {
    id: number;
    label: string;
    loading: boolean;
    name: string;
    type: AttachType;
    uuid: string;
    uploadDateTime: Date;
    safeUrl: SafeResourceUrl;
    chiamataPreAdesioneDateTime: Date;


    constructor(att: IContractAttachment) {
        this.id = att.id;
        this.label = att.label;
        this.loading = att.loading;
        this.name = att.name;
        this.type = att.type;
        this.uploadDateTime = att.uploadDateTime;
        this.safeUrl = att.safeUrl;
        this.chiamataPreAdesioneDateTime = att.chiamataPreAdesioneDateTime != null
            ? new Date(att.chiamataPreAdesioneDateTime)
            : null;
        //always set an uuid
        if (att.id != null) {
            this.uuid = att.id.toString();
        } else if (att.uuid != null) {
            this.uuid = att.uuid;
        } else {
            this.uuid = uuidv5();
        }
    }
}

export interface IContractAttachmentNotMapped extends IContractAttachment {
    tmpType: AttachType;
}

export class AttachmentNotMapped extends Attachment implements IContractAttachmentNotMapped {
    tmpType: AttachType;

    constructor(att: IContractAttachment) {
        super(att);
    }

}

export class ContractAttachments {
    private static readonly mapAttachTypeToLabel: Map<AttachType, string> = new Map<
        AttachType,
        string
    >([
        [AttachType.CartaIdentita, 'Documento di identità'],
        [AttachType.CartaIdentitaElettronica, 'Documento di identità'],
        [AttachType.Patente, 'Documento di identità'],
        [AttachType.Passaporto, 'Documento di identità'],
        [AttachType.RiconoscimentoGenerico, 'Documento di identità'],
        [AttachType.AgevolazioneAccise, 'Accise'],
        [AttachType.CondizioniEconomiche, 'Condizioni economiche'],
        [AttachType.ContrattoCartaceo, 'Contratto cartaceo'],
        [AttachType.DatiCatastali, 'Dati catastali'],
        [AttachType.IvaAgevolata, 'Agevolazioni'],
        [AttachType.RichiestaFornitura, 'Richiesta fornitura'],
        [AttachType.FileAudioFirma, 'Firma audio'],
        [AttachType.AllegatoGenerico, 'Allegato generico'],
        [AttachType.CodiceFiscale, 'Documento d\'identità e codice fiscale'],
        [AttachType.UltimaBolletta, 'Ultima bolletta'],
        [AttachType.VisuraCamerale, 'Visura camerale'],
        [AttachType.RegistrazionePreAdesione, 'Registrazione chiamata di pre-vendita'],
        [AttachType.RegistrazioneAdesione, 'Registrazione adesione contrattuale'],
        [AttachType.ChiamataVerifica, 'Chiamata di verifica'],
        [AttachType.AllegatiRegistrazionePreAdesione, 'Allegati alla chiamata di pre-vendita'],
        [AttachType.AllegatiRegistrazioneAdesione, 'Allegati alla chiamata di adesione'],
        [AttachType.SmsTeleselling, 'Sms teleselling'],
        [AttachType.VocalOrder, 'Registrazione vocal order'],
        [AttachType.Delega, 'Delega'],
        [AttachType.ServizioUltimaIstanza, 'Identificazione del servizio di ultima istanza'],
        [AttachType.LegittimaOccupazione, 'Documento o autocertificazione legittima occupazione immobile']
    ]);

    private readonly _documento_identita: IContractAttachment[];
    private readonly _condizioni_economiche: IContractAttachment[];
    private readonly _dati_catastali: IContractAttachment[];
    private readonly _iva: IContractAttachment[];
    private readonly _accise: IContractAttachment[];
    private readonly _contratto_cartaceo: IContractAttachment[];
    private readonly _richiesta_fornitura: IContractAttachment[];
    private readonly _firma_file_audio: IContractAttachment[];
    private readonly _allegato_generico: IContractAttachment[];
    private readonly _codice_fiscale: IContractAttachment[];
    private readonly _ultima_bolletta: IContractAttachment[];
    private readonly _visura_camerale: IContractAttachment[];
    private readonly _registrazione_pre_adesione: IContractAttachment[];
    private readonly _registrazione_adesione: IContractAttachment[];
    private readonly _chiamata_verifica: IContractAttachment[];
    private readonly _allegati_registrazione_pre_adesione: IContractAttachment[];
    private readonly _allegati_registrazione_adesione: IContractAttachment[];
    private readonly _sms_teleselling: IContractAttachment[];
    private readonly _vocal_order: IContractAttachment[];
    private readonly _delega: IContractAttachment[];
    private readonly _servizio_ultima_istanza: IContractAttachment[];
    private readonly _legittima_occupazione: IContractAttachment[];
    private readonly _non_mappati: IContractAttachmentNotMapped[];

    constructor() {
        this._documento_identita = [];
        this._condizioni_economiche = [];
        this._dati_catastali = [];
        this._iva = [];
        this._accise = [];
        this._contratto_cartaceo = [];
        this._richiesta_fornitura = [];
        this._firma_file_audio = [];
        this._allegato_generico = [];
        this._codice_fiscale = [];
        this._ultima_bolletta = [];
        this._visura_camerale = [];
        this._registrazione_pre_adesione = [];
        this._allegati_registrazione_pre_adesione = [];
        this._registrazione_adesione = [];
        this._allegati_registrazione_adesione = [];
        this._chiamata_verifica = [];
        this._sms_teleselling = [];
        this._vocal_order = [];
        this._delega = [];
        this._servizio_ultima_istanza = [];
        this._legittima_occupazione = [];
        this._non_mappati = [];
    }

    private static _getAttachmentLabel(type: AttachType): string {
        try {
            return ContractAttachments.mapAttachTypeToLabel.get(type);
        } catch (e) {
            console.error(`AttachmentsMeta TypeError. type=${type}`);
            return 'Allegato non identificato';
        }
    }

    static fromContractFiles(files: IShadowStorageFileAttach[]): ContractAttachments {
        const attMeta = new ContractAttachments();
        for (const file of files) {
            const att: IContractAttachment = {
                id: file.id,
                name: file.filename,
                type: file.idAttachType,
                loading: false,
                uuid: null,
                label: ContractAttachments._getAttachmentLabel(file.idAttachType),
                uploadDateTime: file.uploadDateTime,
                safeUrl: null,
                chiamataPreAdesioneDateTime: file.chiamataPreAdesioneDateTime
            };
            if (file.idAttachType != null) {
                attMeta.pushAttachment(
                    new Attachment(att)
                );
            } else {
                attMeta.pushAttachment(
                    new AttachmentNotMapped(att)
                );
            }

        }
        return attMeta;
    }

    get accise(): IContractAttachment[] {
        return this._accise;
    }
    get condizioni_economiche(): IContractAttachment[] {
        return this._condizioni_economiche;
    }
    get contratto_cartaceo(): IContractAttachment[] {
        return this._contratto_cartaceo;
    }
    get dati_catastali(): IContractAttachment[] {
        return this._dati_catastali;
    }
    get documento_identita(): IContractAttachment[] {
        return this._documento_identita;
    }
    get iva(): IContractAttachment[] {
        return this._iva;
    }
    get non_mappati(): IContractAttachmentNotMapped[] {
        return this._non_mappati;
    }
    get richiesta_fornitura(): IContractAttachment[] {
        return this._richiesta_fornitura;
    }
    get firma_file_audio(): IContractAttachment[] {
        return this._firma_file_audio;
    }
    get allegato_generico(): IContractAttachment[] {
        return this._allegato_generico;
    }
    get codice_fiscale(): IContractAttachment[] {
        return this._codice_fiscale;
    }
    get ultima_bolletta(): IContractAttachment[] {
        return this._ultima_bolletta;
    }
    get visura_camerale(): IContractAttachment[] {
        return this._visura_camerale;
    }
    get registrazione_pre_adesione(): IContractAttachment[] {
        return this._registrazione_pre_adesione;
    }
    get registrazione_adesione(): IContractAttachment[] {
        return this._registrazione_adesione;
    }
    get chiamata_verifica(): IContractAttachment[] {
        return this._chiamata_verifica;
    }
    get allegati_registrazione_pre_adesione(): IContractAttachment[] {
        return this._allegati_registrazione_pre_adesione;
    }
    get allegati_registrazione_adesione(): IContractAttachment[] {
        return this._allegati_registrazione_adesione;
    }
    get sms_teleselling(): IContractAttachment[] {
        return this._sms_teleselling;
    }
    get vocal_order(): IContractAttachment[] {
        return this._vocal_order;
    }
    get delega(): IContractAttachment[] {
        return this._delega;
    }
    get servizio_ultima_istanza(): IContractAttachment[] {
        return this._servizio_ultima_istanza;
    }
    get legittima_occupazione(): IContractAttachment[] {
        return this._legittima_occupazione;
    }

    private _getArrayFromAttachType(attType: AttachType): IContractAttachment[] {
        if (attType != null) {
            attType = Number(attType);
        }
        switch (attType) {
            case AttachType.CartaIdentita:
                return this.documento_identita;
            case AttachType.CartaIdentitaElettronica:
                return this.documento_identita;
            case AttachType.Patente:
                return this.documento_identita;
            case AttachType.Passaporto:
                return this.documento_identita;
            case AttachType.RiconoscimentoGenerico:
                return this.documento_identita;
            case AttachType.AgevolazioneAccise:
                return this.accise;
            case AttachType.CondizioniEconomiche:
                return this.condizioni_economiche;
            case AttachType.ContrattoCartaceo:
                return this.contratto_cartaceo;
            case AttachType.DatiCatastali:
                return this.dati_catastali;
            case AttachType.IvaAgevolata:
                return this.iva;
            case AttachType.RichiestaFornitura:
                return this.richiesta_fornitura;
            case AttachType.FileAudioFirma:
                return this.firma_file_audio;
            case AttachType.AllegatoGenerico:
                return this.allegato_generico;
            case AttachType.CodiceFiscale:
                return this.codice_fiscale;
            case AttachType.UltimaBolletta:
                return this.ultima_bolletta;
            case AttachType.VisuraCamerale:
                return this.visura_camerale;
            case AttachType.RegistrazionePreAdesione:
                return this.registrazione_pre_adesione;
            case AttachType.RegistrazioneAdesione:
                return this.registrazione_adesione;
            case AttachType.ChiamataVerifica:
                return this.chiamata_verifica;
            case AttachType.AllegatiRegistrazionePreAdesione:
                return this.allegati_registrazione_pre_adesione;
            case AttachType.AllegatiRegistrazioneAdesione:
                return this.allegati_registrazione_adesione;
            case AttachType.SmsTeleselling:
                return this.sms_teleselling;
            case AttachType.VocalOrder:
                return this.vocal_order;
            case AttachType.Delega:
                return this.delega;
            case AttachType.ServizioUltimaIstanza:
                return this.servizio_ultima_istanza;
            case AttachType.LegittimaOccupazione:
                return this.legittima_occupazione;
            case null:
            case undefined:
                return this.non_mappati;
            default:
                console.error(`Cannot get array corresponding to attachType=${attType}`);
                return this.non_mappati;
        }
    }

    private _removeAttachmentByUUID(attachment: IContractAttachment) {
        const arr = this._getArrayFromAttachType(attachment.type);
        const index = arr.findIndex(att => att.uuid === attachment.uuid);
        if (index !== -1) {
            arr.splice(index, 1);
        } else {
            console.error(
                `AttachmentsMeta ValueError. uuid=${attachment.uuid}, type=${attachment.type}`
            );
        }
    }

    setLoading(attachment: IContractAttachment, loading: boolean): void {
        const arr = this._getArrayFromAttachType(attachment.type);
        const index = arr.findIndex(att => att.uuid === attachment.uuid);
        if (index !== -1) {
            arr[index].loading = loading;
        } else {
            console.error(
                `AttachmentsMeta ValueError. uuid=${attachment.uuid}, type=${attachment.type}`
            );
        }
    }

    setId(attachment: IContractAttachment, id: number): void {
        const arr = this._getArrayFromAttachType(attachment.type);
        const index = arr.findIndex(att => att.uuid === attachment.uuid);
        if (index !== -1) {
            arr[index].id = id;
        } else {
            console.error(
                `AttachmentsMeta ValueError. uuid=${attachment.uuid}, type=${attachment.type}`
            );
        }
    }
    setUploadedDateTime(attachment: IContractAttachment, uploadedDateTime: Date): void {
        const arr = this._getArrayFromAttachType(attachment.type);
        const index = arr.findIndex(att => att.uuid === attachment.uuid);
        if (index !== -1) {
            arr[index].uploadDateTime = uploadedDateTime;
        } else {
            console.error(
                `AttachmentsMeta ValueError. uuid=${attachment.uuid}, type=${attachment.type}`
            );
        }
    }

    setSafeUrl(attachment: IContractAttachment, safeUrl: SafeResourceUrl): void {
        const arr = this._getArrayFromAttachType(attachment.type);
        const index = arr.findIndex(att => att.uuid === attachment.uuid);
        if (index !== -1) {
            arr[index].safeUrl = safeUrl;
        } else {
            console.error(
                `AttachmentsMeta ValueError. uuid=${attachment.uuid}, type=${attachment.type}`
            );
        }
    }

    pushAttachment(attachment: IContractAttachment): void {
        const arr = this._getArrayFromAttachType(attachment.type);
        const index = arr.findIndex(file => file.uuid === attachment.uuid);
        //avoid duplicates
        if (index === -1) {
            arr.push(attachment);
        }
    }

    removeAttachment(attachment: IContractAttachment): void {
        this._removeAttachmentByUUID(attachment);
    }

    getAllAttachments(): IContractAttachment[] {
        let arr: IContractAttachment[] = [];
        arr = arr.concat(
            this.accise,
            this.condizioni_economiche,
            this.contratto_cartaceo,
            this.dati_catastali,
            this.documento_identita,
            this.iva,
            this.richiesta_fornitura,
            this.firma_file_audio,
            this.allegato_generico,
            this.codice_fiscale,
            this.ultima_bolletta,
            this.visura_camerale,
            this.registrazione_pre_adesione,
            this.registrazione_adesione,
            this.chiamata_verifica,
            this.allegati_registrazione_adesione,
            this.allegati_registrazione_pre_adesione,
            this.sms_teleselling,
            this.vocal_order,
            this.delega,
            this.servizio_ultima_istanza,
            this.legittima_occupazione,
            this.non_mappati
        );
        return arr;
    }

    setAttachType(attachment: IContractAttachment, attachType: AttachType) {
        const arr = this._getArrayFromAttachType(attachment.type);
        const index = arr.findIndex(att => att.uuid === attachment.uuid);
        if (index !== -1) {
            const _att = arr[index];
            this.removeAttachment(_att);
            _att.type = attachType;
            this.pushAttachment(_att);
        } else {
            console.error(
                `AttachmentsMeta ValueError. uuid=${attachment.uuid}, type=${attachment.type}`
            );
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/helpers/attachmentsMeta.class.ts